import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatDistanceToNow } from "date-fns";
import {
  abandonDeflection, confirmDeflection, createKBArticle, deleteKBArticle,
  getKBArticle, incrementKBView, updateKBArticle,
} from "@/lib/firestore";
import { useAuth } from "@/lib/auth";
import { ALL_PLATFORMS, CATEGORIES, KBArticle, Platform, PLATFORM_LABELS } from "@/types";
import { Spinner, toast } from "@/components/shared";

interface LocationState {
  deflectionId?: string;
}

export default function KbArticlePage({ mode }: { mode: "view" | "new" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { helpDeskUser } = useAuth();
  const isAgent = helpDeskUser?.role === "agent" || helpDeskUser?.role === "admin";
  const isAdmin = helpDeskUser?.role === "admin";

  const deflectionId = (location.state as LocationState | null)?.deflectionId;
  const [deflectionResolved, setDeflectionResolved] = useState(false);

  const editing = mode === "new" || searchParams.get("edit") === "1";

  const [article, setArticle] = useState<KBArticle | null>(null);
  const [loading, setLoading] = useState(mode === "view");
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [body, setBody] = useState("");

  const togglePlatform = (p: Platform) =>
    setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  useEffect(() => {
    if (mode === "new" || !id) { setLoading(false); return; }
    getKBArticle(id).then((a) => {
      if (!a) { toast("Article not found", "error"); navigate("/kb"); return; }
      setArticle(a);
      setTitle(a.title);
      setCategory(a.category);
      setPlatforms(a.platforms ?? []);
      setBody(a.body);
      setLoading(false);
      if (!editing) incrementKBView(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      if (mode === "new") {
        const newId = await createKBArticle({ title: title.trim(), body, category, platforms });
        toast("Article published");
        navigate(`/kb/${newId}`);
      } else if (id) {
        await updateKBArticle(id, { title: title.trim(), body, category, platforms });
        toast("Article saved");
        navigate(`/kb/${id}`);
      }
    } catch (err) {
      toast(`Save failed: ${(err as Error).message}`, "error");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!id || !article) return;
    if (!confirm(`Delete "${article.title}"? This is permanent.`)) return;
    try {
      await deleteKBArticle(id);
      toast("Article deleted");
      navigate("/kb");
    } catch (err) {
      toast(`Delete failed: ${(err as Error).message}`, "error");
    }
  };

  if (loading) {
    return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate("/kb")} style={{ marginBottom: 16 }}>← Back to Knowledge Base</button>

      {editing ? (
        <form onSubmit={handleSave} style={{ maxWidth: 760 }}>
          <h1 style={{ marginBottom: 18 }}>{mode === "new" ? "New Article" : "Edit Article"}</h1>

          <div className="card card-body" style={{ marginBottom: 14 }}>
            <div className="fg">
              <label>Title *</label>
              <input className="fi" value={title} onChange={(e) => setTitle(e.target.value)} required
                placeholder="How to fix VPN after Windows Update" />
            </div>

            <div className="fg">
              <label>Category</label>
              <select className="fsel" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="fg">
              <label>Platforms <span style={{ color: "var(--text2)", fontWeight: 400 }}>— leave empty if it applies to everyone</span></label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ALL_PLATFORMS.map((p) => {
                  const on = platforms.includes(p);
                  return (
                    <button key={p} type="button" onClick={() => togglePlatform(p)}
                      style={{
                        fontSize: 12, padding: "5px 12px", borderRadius: 20,
                        border: `1px solid ${on ? "var(--blue)" : "var(--border-med)"}`,
                        background: on ? "var(--blue)" : "var(--surface)",
                        color: on ? "white" : "var(--text2)",
                        cursor: "pointer", fontFamily: "var(--font)",
                      }}>
                      {PLATFORM_LABELS[p]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="fg">
              <label>Body — markdown supported</label>
              <textarea className="fta" value={body} onChange={(e) => setBody(e.target.value)} required
                style={{ minHeight: 360, fontFamily: "var(--font-mono)", fontSize: 13, lineHeight: 1.55, resize: "vertical" }}
                placeholder={`# Symptoms\n\nWhat the user sees.\n\n# Steps to fix\n\n1. First step\n2. Second step\n   - Sub-bullet\n\nUse \`code\` for commands and **bold** for emphasis.`} />
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 4 }}>
                Supports headings, **bold**, *italic*, `code`, lists, links, tables.
              </div>
            </div>
          </div>

          {body.trim() && (
            <div className="card card-body" style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, marginBottom: 10 }}>Preview</div>
              <div className="md">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={saving || !title.trim() || !body.trim()}>
              {saving ? <Spinner size={14} color="white" /> : mode === "new" ? "Publish Article" : "Save Changes"}
            </button>
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate(mode === "new" ? "/kb" : `/kb/${id}`)}>Cancel</button>
          </div>
        </form>
      ) : article ? (
        <div style={{ maxWidth: 760 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <h1 style={{ marginBottom: 6 }}>{article.title}</h1>
              <div style={{ display: "flex", gap: 10, fontSize: 12, color: "var(--text2)", alignItems: "center", flexWrap: "wrap" }}>
                <span className="badge badge-gem">{article.category}</span>
                {(article.platforms ?? []).map((p) => (
                  <span key={p} className="badge" style={{ background: "var(--gray)", color: "var(--text)" }}>{PLATFORM_LABELS[p]}</span>
                ))}
                <span>{(article.viewCount ?? 0).toLocaleString()} views</span>
                {article.updatedAt && <span>Updated {formatDistanceToNow(article.updatedAt.toDate(), { addSuffix: true })}</span>}
              </div>
            </div>
            {isAgent && (
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/kb/${id}?edit=1`)}>Edit</button>
                {isAdmin && <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>}
              </div>
            )}
          </div>

          <div className="card card-body md" style={{ fontSize: 14, lineHeight: 1.65 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body}</ReactMarkdown>
          </div>

          {deflectionId && !deflectionResolved && (
            <div style={{
              marginTop: 16, padding: "14px 16px",
              background: "var(--green-l)", border: "1px solid rgba(40,205,65,.3)",
              borderRadius: "var(--rs)",
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--green-t)", marginBottom: 4 }}>
                Did this article fix your issue?
              </div>
              <div style={{ fontSize: 12, color: "var(--green-t)", marginBottom: 10, opacity: 0.85 }}>
                Tells us this article helped — no ticket needed.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-green btn-sm"
                  onClick={async () => {
                    try {
                      await confirmDeflection(deflectionId);
                      setDeflectionResolved(true);
                      toast("Glad it helped! 🎉");
                    } catch (e) {
                      toast(`Could not save: ${(e as Error).message}`, "error");
                    }
                  }}>
                  ✓ Yes, this fixed it
                </button>
                <button className="btn btn-secondary btn-sm"
                  onClick={async () => {
                    try { await abandonDeflection(deflectionId); } catch { /* not critical */ }
                    navigate("/tickets/new");
                  }}>
                  No, I still need help →
                </button>
              </div>
            </div>
          )}

          {deflectionResolved && (
            <div style={{ marginTop: 16, padding: "12px 16px",
              background: "var(--green-l)", borderRadius: "var(--rs)",
              fontSize: 13, color: "var(--green-t)" }}>
              ✓ Marked as resolved by this article. Thanks for the feedback!
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
